import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { featuredModuleCards, MODULE_THEME } from '@/data/modules'
import { SEO } from '@/components/seo'
import ModuleIcon from './ModuleIcon'

/**
 * Full-bleed expanding-panel selector for the five core modules (Land Rover
 * "explore the range" style). Hover or tap a panel to expand it; click into it
 * to open the module's showcase page.
 */
export default function ModuleExplorePage() {
  const navigate = useNavigate()
  const [active, setActive] = useState(0)

  const handlePanelClick = (index: number, slug: string) => {
    // First interaction expands; clicking the already-open panel enters it.
    if (active === index) navigate(`/product/${slug}`)
    else setActive(index)
  }

  return (
    <>
      <SEO
        title="Explore the Core Modules | WealthSpace"
        description="Explore WealthSpace's five core modules: Meetings, AI Report Writer, Sparks AI Assistant, Ramble Dictation and Client Management. Click into each to see use cases, benefits and pricing."
        keywords="WealthSpace modules, financial adviser software, explore IFA platform"
        url="/product/explore"
      />

      {/* Intro */}
      <section className="px-6 pt-16 pb-8 text-center">
        <p className="text-xs font-semibold text-accent-teal tracking-[0.15em] uppercase mb-3">
          Explore the range
        </p>
        <h1 className="font-serif text-[clamp(32px,4.5vw,48px)] leading-[1.12] text-slate-900 mb-3">
          Five core modules. One platform.
        </h1>
        <p className="text-lg text-slate-600 max-w-[600px] mx-auto">
          Hover or tap a panel to take a closer look, then click in to explore the module.
        </p>
      </section>

      {/* Full-bleed selector */}
      <section className="px-0 pb-6">
        <div className="flex flex-col md:flex-row md:h-[640px] w-full overflow-hidden md:gap-1.5 px-0 md:px-1.5">
          {featuredModuleCards.map((module, index) => {
            const theme = MODULE_THEME[module.colorClass]
            const isActive = active === index
            return (
              <div
                key={module.slug}
                onMouseEnter={() => setActive(index)}
                onClick={() => handlePanelClick(index, module.slug)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/product/${module.slug}`)
                }}
                aria-label={`${module.name}: ${module.tagline}`}
                className={`group relative overflow-hidden cursor-pointer outline-none min-h-[150px] md:min-h-0 flex-1 transition-all duration-500 ease-out md:rounded-2xl first:rounded-t-2xl last:rounded-b-2xl md:first:rounded-2xl md:last:rounded-2xl ${
                  isActive ? 'md:flex-[3.2]' : 'md:flex-[1]'
                }`}
                style={{ background: theme.heroBackground }}
              >
                {/* Dim inactive panels for focus: desktop only (mobile stacks
                    them all open, so dimming would just grey out real content). */}
                <div
                  aria-hidden
                  className={`absolute inset-0 bg-slate-950 transition-opacity duration-500 opacity-0 ${
                    isActive ? '' : 'md:opacity-25'
                  }`}
                />

                {/* Icon, top */}
                <div className="absolute top-6 left-1/2 -translate-x-1/2 text-white/90 z-[2]">
                  <ModuleIcon name={module.icon} className="w-7 h-7" />
                </div>

                {/* Collapsed vertical label (desktop) */}
                <div
                  className={`hidden md:flex absolute inset-0 items-end justify-center pb-8 z-[2] transition-opacity duration-300 ${
                    isActive ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  <span className="text-white font-serif text-xl tracking-wide whitespace-nowrap [writing-mode:vertical-rl] rotate-180">
                    {module.name}
                  </span>
                </div>

                {/* Expanded content: always shown on mobile, active-only on desktop */}
                <div
                  className={`absolute inset-x-0 bottom-0 p-7 md:p-9 z-[2] transition-all duration-300 opacity-100 translate-y-0 ${
                    isActive ? '' : 'md:opacity-0 md:translate-y-4 md:pointer-events-none'
                  }`}
                >
                  <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">{module.name}</h2>
                  <p className="text-white/80 text-sm leading-relaxed mb-5 max-w-[320px]">
                    {module.tagline}
                  </p>
                  <Link
                    to={`/product/${module.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-md font-semibold text-sm no-underline transition-transform duration-200 hover:-translate-y-0.5"
                  >
                    Explore {module.name}
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Footer links */}
      <div className="px-6 pb-16 text-center">
        <Link
          to="/product"
          className="inline-flex items-center gap-2 text-sm font-semibold text-accent-teal no-underline hover:text-accent-tealDark"
        >
          See all modules &amp; pricing
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </>
  )
}
